import React, { useCallback, useRef, useEffect } from 'react';
import { isPartialSlideMode } from './Items';
import { useCarousel } from '../state';
import { isAboveFold } from '../Item';

export interface MultiFrameItemProps {
  children: React.ReactElement | React.ReactElement[] | string;
  className?: string;
  itemWidth?: number;
  fullWidthMode?: boolean;
  partialNextSlideWidth?: number;
  index: number;
  role?: string;
  ariaControls?: string;
  id?: string;
  ariaHidden?: boolean;
}

const Item = ({
  children,
  className,
  itemWidth,
  fullWidthMode = false,
  partialNextSlideWidth = 0,
  index,
  role,
  ariaControls,
  id,
}: MultiFrameItemProps): React.ReactElement => {
  const [state] = useCarousel();
  const itemRef = useRef<HTMLLIElement>(null);
  const isBackward = useRef(false);
  const totalItemCnt = state.totalItems;
  const fullItemWidth = `${100 / totalItemCnt}%`;
  const currIndex = state.currentIndex;
  const needPartialSlide = isPartialSlideMode(partialNextSlideWidth);
  const ariaHiddenVal = index === currIndex ? 'false' : 'true';
  const isLastSlide = currIndex ? currIndex >= totalItemCnt - 1 : false;

  if (isLastSlide) {
    isBackward.current = true;
  } else if (currIndex === 0) {
    isBackward.current = false;
  }

  let widthVal;

  const calcWidthPartialItemMode = useCallback(() => {
    if (currIndex === index) {
      return `calc(${fullItemWidth} - ${partialNextSlideWidth}px)`;
    }

    if (isLastSlide || isBackward.current) {
      if (currIndex === index + 1) {
        return `calc(${fullItemWidth} + ${partialNextSlideWidth}px)`;
      }
    } else {
      if (currIndex === index - 1) {
        return `calc(${fullItemWidth} + ${partialNextSlideWidth}px)`;
      }
    }

    return fullItemWidth;
  }, [currIndex, index, isLastSlide, fullItemWidth, partialNextSlideWidth]);

  if (needPartialSlide) {
    widthVal = calcWidthPartialItemMode();
  } else {
    widthVal = fullWidthMode ? fullItemWidth : `${itemWidth}px`;
  }

  useEffect(() => {
    const itemEle = itemRef && itemRef.current;
    if (index === currIndex && itemEle && isAboveFold(itemEle)) {
      // without setTimeout carousel generates blank slides under certain conditions.
      setTimeout(() => {
        itemEle.focus();
      }, 500);
    }
  }, [currIndex, index]);

  return (
    <li
      id={id}
      ref={itemRef}
      className={className}
      style={{
        flexGrow: 1,
        padding: 0,
        margin: 0,
        width: widthVal,
      }}
      tabIndex={-1}
      aria-hidden={ariaHiddenVal}
      role={role}
      aria-controls={ariaControls}
    >
      {children}
    </li>
  );
};

Item.displayName = 'CarouselItem';
export default Item;
